import { makeDirWith, getTempDirInUserDataWith } from '@mimir/client-common/upload/premiere';
import type { LoadedItem } from '@mimir/common/dto/searchResultHit';
import { getDirname } from '@mimir/common/platformAbstraction/pathComposition';
import { writeFile } from '@mimir/common/util/fs';
import { getGlobalStore } from '../store';
import { importToPremiereAsync } from './importToPremiere';
import { getSrtLocalPath } from './localState';
import type { AdobePanelContext, ElectronContext } from '@mimir/common/context/applicationContext';
import type { Logger } from '@mimir/common/types/logger';
import { getTitleBasedOnRange } from '../util/timelineUtils';
import type { FuzzyValue } from '@mimir/common/util/fuzzyNumbers';

export async function writeAndImportSrt(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  userDataSystemPath: string,
  item: LoadedItem,
  srtString: string,
  ext: string,
  timeRangePrefix: string,
  virtualClipStart: FuzzyValue | null,
  durationInMs: number,
) {
  const pathSep = services.applicationContext.pathSep;

  // note: we append timestamp to srt file name since it is often found to be locked by the Premiere process and
  // hence makes subsequent imports of the same srt fail.
  let srtLocalPath = await getSrtLocalPath(item, ext, timeRangePrefix, pathSep);
  const parentDir = getDirname(srtLocalPath, pathSep);
  services.logger.debug(`srtLocalPath: ${parentDir}`);

  try {
    await makeDirWith(services, parentDir);
    writeFile(services.applicationContext.nodeApi, srtLocalPath, srtString);
  } catch (err) {
    const tempDirPath = await getTempDirInUserDataWith(services, userDataSystemPath);
    srtLocalPath = `${tempDirPath}${pathSep}${timeRangePrefix}${item.id}_${+new Date()}.${ext}`;
    services.logger.debug(`Try writing ${srtLocalPath} since previous path failed with: ${err}`);
    writeFile(services.applicationContext.nodeApi, srtLocalPath, srtString);
  }

  const store = getGlobalStore();
  const shouldUsePremierePreferences = store.getters.usePremierePreferenceForScaleToFrame;

  const titleBasedOnRange = getTitleBasedOnRange(
    store.getters.item,
    store.getters.timeline,
    virtualClipStart,
    durationInMs,
    false,
  );
  const title = `${titleBasedOnRange} captions`;
  await importToPremiereAsync(
    services.applicationContext,
    {
      filePath: srtLocalPath,
      title,
      artifactReference: null,
      startTimeInTicks: null,
      timeDisplayFormat: null,
      timelineInsertion: null,
    },
    shouldUsePremierePreferences
  );
}
